import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, BedDouble, CarFront, Heart } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { Photo } from '@/components/ui/Photo';
import { Lotus } from '@/components/ui/Lotus';
import { useBusiness } from '@/lib/business';
import { useImages, type ImageMap } from '@/lib/images';
import { useReveal } from '@/lib/useReveal';

type Product = {
  to: string;
  tag: string;
  title: string;
  text: string;
  icon: typeof Sparkles;
  imgKey: keyof ImageMap;
};

const PRODUCTS: Product[] = [
  {
    to: '/spa', tag: 'The Spa', title: 'Spa & Beauty', icon: Sparkles, imgKey: 'spaPillar',
    text: 'Massage, facials, nails, and makeup — unhurried, tailored treatments in a calm setting.',
  },
  {
    to: '/stay', tag: 'The Stays', title: 'Rooms & BnB', icon: BedDouble, imgKey: 'stayPillar',
    text: 'Self-catering comfort beside the spa — a pool, garden, fast WiFi, and a restful night.',
  },
  {
    to: '/cars', tag: 'Car Hire', title: 'Vehicles by the day', icon: CarFront, imgKey: 'carPillar',
    text: 'Reliable, well-kept cars for business or leisure — compact hatchbacks to 4x4 SUVs.',
  },
];

export function Landing() {
  useReveal();
  const biz = useBusiness();
  const img = useImages();

  return (
    <>
      <Seo title={`${biz.name} — Spa · Stays · Car Hire`} description="Grace Haven in Gaborone — a wellness spa, a self-catering BnB, and car hire. Choose your haven." />

      {/* Umbrella hero (dark, so the overlay nav stays legible) */}
      <section className="relative flex min-h-[62vh] items-center justify-center overflow-hidden bg-espresso-deep pt-24 text-center">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(70% 60% at 50% 35%, rgba(201,150,46,0.16), transparent 62%)' }} />
        <Container className="relative z-10">
          <Lotus size={46} className="mx-auto text-gold" />
          <p className="eyebrow mt-5 text-gold-soft">{biz.city}, {biz.country}</p>
          <h1 className="mt-3 font-display text-5xl font-medium leading-[1.05] text-cream sm:text-6xl">
            One <span className="wordmark text-[1.15em] text-gold">haven</span>, three ways to feel at home.
          </h1>
          <div className="rule-gold mx-auto mt-7" />
          <p className="mx-auto mt-6 max-w-xl font-body text-lg font-light leading-relaxed text-cream/85">
            {biz.wordmark} brings together a wellness spa, a restful BnB, and car hire in {biz.city} — relax, stay, and get around, all in one place. Choose where you'd like to begin.
          </p>
        </Container>
      </section>

      {/* Three product cards */}
      <section className="bg-cream py-20 sm:py-24">
        <Container>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p) => (
              <Link key={p.to} to={p.to} className="reveal group relative block overflow-hidden">
                <Photo src={img[p.imgKey]} alt={p.title} className="aspect-[3/4] w-full" imgClassName="transition duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/92 via-espresso-deep/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <p.icon size={26} className="text-gold" strokeWidth={1.5} />
                  <p className="eyebrow mt-3 text-gold-soft">{p.tag}</p>
                  <h2 className="mt-1 font-display text-3xl text-cream">{p.title}</h2>
                  <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-cream/80">{p.text}</p>
                  <span className="btn-gold mt-5 !px-6 !py-2.5">Explore <ArrowRight size={15} /></span>
                </div>
              </Link>
            ))}
          </div>

          <p className="mt-12 text-center font-body text-sm text-muted">
            Looking to book?{' '}
            <Link to="/book" className="font-medium text-gold-deep hover:underline">Start a booking</Link>
            {' '}· or{' '}
            <Link to="/contact" className="font-medium text-gold-deep hover:underline">get in touch</Link>.
          </p>

          <p className="mt-10 flex flex-wrap items-center justify-center gap-1.5 text-center font-body text-xs text-muted">
            Made with <Heart size={12} className="fill-blush-deep text-blush-deep" /> in Botswana by{' '}
            <a href="https://acepsle-hub.web.app/" target="_blank" rel="noopener noreferrer" className="font-medium text-gold-deep hover:underline">
              AcePSLE (Pty) Ltd
            </a>
          </p>
        </Container>
      </section>
    </>
  );
}
